<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcWorksheetWorkItems',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    worksheet: {
      type: Object,
      required: true,
    },
    isReadonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const onServiceSelected = (...args) => {
    const [row, service] = args;

    row.data.service = service;
    row.data.service_id = service.id;
    row.data.unit = service.unit;
    row.data.task = service.name;
    row.data.tax_code = service.sales_vat_code;
    row.data.intermediated_service = service.intermediated_service || false;
    row.setFocus('quantity');
  };
  const onServiceChanged = (...args) => {
    console.log('onServiceChanged', args);
  };
  const calculateUnitPrice = (...args) => {
    const [row, tax, decimals] = args;

    row.data.unit_price = Big(
      Big(row.data.net_amount) /
        Big(row.data.quantity) /
        (1 - Big(row.data.discount) / 100)
    )
      .round(decimals)
      .toNumber();
  };
  const calculateNetAmount = (...args) => {
    const [row, tax, decimals] = args;

    row.data.net_amount = Big(
      Big(row.data.quantity) *
        Big(row.data.unit_price) *
        (1 - Big(row.data.discount) / 100)
    )
      .round(decimals)
      .toNumber();
  };
  const calculateNetAmountFromAmount = (...args) => {
    const [row, tax, decimals] = args;

    row.data.net_amount = Big(Big(row.data.amount) / (1 + Big(tax.rate) / 100))
      .round(decimals)
      .toNumber();
  };
  const calculateAmount = (...args) => {
    const [row, tax, decimals] = args;

    row.data.amount = Big(Big(row.data.net_amount) * (1 + Big(tax.rate) / 100))
      .round(decimals)
      .toNumber();
  };
  const recalculateLineItem = (...args) => {
    const data = args[0].data;
    const row = data.row;
    const decimals = 2;
    const tx = _.find(taxCodes.value, { id: row.data.tax_code });

    if (row.data.quantity === 0) {
      return;
    }

    switch (data.field) {
      case 'quantity':
      case 'unit_price':
      case 'discount': {
        calculateNetAmount(row, tx, decimals);
        calculateAmount(row, tx, decimals);
        break;
      }

      case 'net_amount': {
        calculateUnitPrice(row, tx, decimals);
        calculateAmount(row, tx, decimals);
        break;
      }

      case 'tax_code': {
        calculateAmount(row, tx, decimals);
        break;
      }

      case 'amount': {
        calculateNetAmountFromAmount(row, tx, decimals);
        calculateUnitPrice(row, tx, decimals);
        break;
      }
    }
  };

  const expenseTypes = ref(null);
  const taxCodes = ref(null);
  const units = ref(null);
  const ux = ref({
    errorHandling: 'local',
    initialLoadSpinner: true,
  });
  const b_5962862420439144_rowHeader = computed(() => false);
  const b_5962862420439144_paging = computed(() => false);
  const b_5962862420439144_sortable = computed(() => false);
  const b_5962862420439144_columns = computed(() => [
    {
      name: 'service',
      type: 'select2',
      header: $fn.value.gettext('Task'),
      width: '140px',
      formatter: {
        name: 'template',
        value:
          '<b-link v-if="row.service_id || (row.service && row.service.id)" :to="{\n\
  name: \'app.product\',\n\
  params: { id: row.service_id || row.service.id }\n\
}">{{ row.task }}</b-link>\n\
<span v-else>{{ row.task }}</span>\n\
',
      },
      options: $fn.value.searchDataSource('suggest_product', {
        parameters: { product_types: 'service' },
      }),
      optionValueField: 'name',
      valueField: 'id',
      textField: 'name',
      bindObject: true,
      validator: ['required'],
    },
    {
      name: 'asset',
      type: 'select2',
      header: $fn.value.gettext('Asset'),
      width: '140px',
      formatter: {
        name: 'template',
        value:
          "<b-link v-if=\"row.asset_id || (row.asset && row.asset.id)\" :to=\"{\n\
  name: 'app.fixedAsset',\n\
  params: { id: row.asset_id || row.asset.id }\n\
}\">{{ row.asset ? row.asset.name : '-' }}</b-link>\n\
<span v-else>{{ row.asset ? row.asset.name : '-' }}</span>\n\
",
      },
      options: $fn.value.searchDataSource('suggest_fixed_asset'),
      optionValueField: 'name',
      valueField: 'id',
      textField: 'name',
      bindObject: true,
    },
    {
      name: 'date',
      type: 'date',
      header: $fn.value.gettext('Date'),
      width: '140px',
      required: true,
    },
    {
      name: 'notes',
      type: 'textarea',
      width: '200px',
      header: $fn.value.gettext('Notes'),
    },
    {
      name: 'quantity',
      type: 'number',
      default: 0,
      header: $fn.value.pgettext('Quantity Abbr', 'Qty'),
      headerTooltip: $fn.value.gettext('Quantity'),
      width: '60px',
      textAlign: 'right',
      required: true,
      editorIsDisabled: (row) => !row.service || !row.service.id,
    },
    {
      name: 'unit',
      header: $fn.value.pgettext('Unit Abbr', 'Unit'),
      headerTooltip: $fn.value.gettext('Unit'),
      width: '70px',
      textAlign: 'center',
      required: true,
      editorIsDisabled: (row) => !row.service || !row.service.id,
    },
    {
      name: 'unit_price',
      type: 'number',
      default: 0,
      header: $fn.value.gettext('Price'),
      width: '70px',
      textAlign: 'right',
      required: true,
      editorIsDisabled: (row) => !row.service || !row.service.id,
    },
    {
      name: 'discount',
      type: 'number',
      default: 0,
      header: $fn.value.pgettext('AbbrDiscount', 'Disc'),
      headerTooltip: $fn.value.gettext('Discount'),
      width: '70px',
      required: true,
      editorIsDisabled: (row) => !row.service || !row.service.id,
    },
    {
      name: 'net_amount',
      type: 'number',
      default: 0,
      header: $fn.value.gettext('Net'),
      width: '100px',
      textAlign: 'right',
      required: true,
      editorIsDisabled: (row) => !row.service || !row.service.id,
    },
    {
      name: 'amount',
      type: 'number',
      default: 0,
      header: $fn.value.gettext('Amount'),
      width: '100px',
      textAlign: 'right',
      required: true,
      editorIsDisabled: (row) => !row.service || !row.service.id,
    },
    {
      name: 'tax_code',
      header: $fn.value.gettext('VAT'),
      headerTooltip: $fn.value.gettext('VAT Code'),
      width: '100px',
      type: 'select',
      options: taxCodes.value,
      valueField: 'id',
      textField: 'id',
      editorIsDisabled: (row) => !row.service || !row.service.id,
    },
    {
      name: 'intermediated_service',
      header: $fn.value.pgettext('AbbrIntermediatedService', 'IMS'),
      headerTooltip: $fn.value.gettext('Intermediated Service'),
      width: '60px',
      type: 'checkbox',
      editorIsDisabled: (row) => !row.service || !row.service.id,
    },
  ]);

  const $data = computed(() => ({
    expenseTypes: expenseTypes.value,
    taxCodes: taxCodes.value,
    units: units.value,
    ux: ux.value,
    b_5962862420439144_rowHeader: b_5962862420439144_rowHeader.value,
    b_5962862420439144_paging: b_5962862420439144_paging.value,
    b_5962862420439144_sortable: b_5962862420439144_sortable.value,
    b_5962862420439144_columns: b_5962862420439144_columns.value,
  }));

  const $$load_expenseTypes = () => {
    return $fn.value.fetch('list_object_by_type', {
      parameters: { object_type: 'expense-type' },
    });
  };
  const $$load_taxCodes = () => {
    return $fn.value.fetch('list_tax_code');
  };
  const $$load_units = () => {
    return $fn.value.fetch('list_unit');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'expenseTypes' },
      { name: 'taxCodes' },
      { name: 'units' },
      { name: 'ux' },
      { name: 'b_5962862420439144_rowHeader', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_paging', nodeId: '5962862420439144' },
      { name: 'b_5962862420439144_sortable', nodeId: '5962862420439144' },
      {
        name: 'b_5962862420439144_columns',
        nodeId: '5962862420439144',
        dependsOn: ['taxCodes'],
      },
    ],
    loaders: {
      expenseTypes: $$load_expenseTypes,
      taxCodes: $$load_taxCodes,
      units: $$load_units,
    },
    ux: { errorHandling: 'local', initialLoadSpinner: true },
    dataRefs: {
      expenseTypes,
      taxCodes,
      units,
      ux,
      b_5962862420439144_rowHeader,
      b_5962862420439144_paging,
      b_5962862420439144_sortable,
      b_5962862420439144_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.worksheet,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('worksheet');
    }
  );
  watch(
    () => props.isReadonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('isReadonly');
    }
  );

  const act_5962862420439144_emit_nkxfz1316_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_5962862420439144_script_95nad8wr7_value_callable = async (
    $event
  ) => {
    return props.worksheet.id
      ? $event.data.waitFor(
          $fn.value
            .crud('work_item')
            .upsert({ ...$event.data.row, worksheet_id: props.worksheet.id })
        )
      : props.worksheet.work_items.splice(
          props.worksheet.work_items.length - 1,
          0,
          $event.data.row
        );
  };
  const act_5962862420439144_script_95nad8wr7_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_95nad8wr7_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_29jq0qetp_value_callable = async (
    $event
  ) => {
    return props.worksheet.id
      ? $event.data.waitFor($fn.value.crud('work_item').delete($event.data.row))
      : _.remove(props.worksheet.work_items, $event.data.row);
  };
  const act_5962862420439144_script_29jq0qetp_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_29jq0qetp_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_r9uuumm2k_value_callable = async (
    $event
  ) => {
    return onServiceSelected($event.data.row, $event.data.option);
  };
  const act_5962862420439144_script_r9uuumm2k_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_r9uuumm2k_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_1w1nhlcuq_value_callable = async (
    $event
  ) => {
    return onServiceChanged($event);
  };
  const act_5962862420439144_script_1w1nhlcuq_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_1w1nhlcuq_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_tp30bhqwr_value_callable = async (
    $event
  ) => {
    return recalculateLineItem($event);
  };
  const act_5962862420439144_script_tp30bhqwr_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_tp30bhqwr_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_sh0fz8mfh_value_callable = async (
    $event
  ) => {
    return recalculateLineItem($event);
  };
  const act_5962862420439144_script_sh0fz8mfh_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_sh0fz8mfh_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_s85myjghy_value_callable = async (
    $event
  ) => {
    return recalculateLineItem($event);
  };
  const act_5962862420439144_script_s85myjghy_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_s85myjghy_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_4p9xw0h3m_value_callable = async (
    $event
  ) => {
    return recalculateLineItem($event);
  };
  const act_5962862420439144_script_4p9xw0h3m_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_4p9xw0h3m_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_auosoz811_value_callable = async (
    $event
  ) => {
    return recalculateLineItem($event);
  };
  const act_5962862420439144_script_auosoz811_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_auosoz811_value_callable($event);
      },
    };
  };
  const act_5962862420439144_script_n6qvghmkf_value_callable = async (
    $event
  ) => {
    return recalculateLineItem($event);
  };
  const act_5962862420439144_script_n6qvghmkf_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_5962862420439144_script_n6qvghmkf_value_callable($event);
      },
    };
  };

  const act_5962862420439144_emit_nkxfz1316 = new actionTypes.EmitAction({
    actionArgs: act_5962862420439144_emit_nkxfz1316_getActionArgs,
  });
  const inst_evh_5962862420439144_saveCompleted_mlcuqt6vm =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_emit_nkxfz1316,
      event: 'save-completed',
      displayName: 'emit',
    });
  const inst_evh_5962862420439144_saveCompleted =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_saveCompleted_mlcuqt6vm],
      event: 'save-completed',
    });
  const act_5962862420439144_script_95nad8wr7 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_95nad8wr7_getActionArgs,
  });
  const inst_evh_5962862420439144_save_grg1k7es4 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_95nad8wr7,
      event: 'save',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_save = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_save_grg1k7es4],
    event: 'save',
  });
  const act_5962862420439144_script_29jq0qetp = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_29jq0qetp_getActionArgs,
  });
  const inst_evh_5962862420439144_delete_a4qx9e42l =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_29jq0qetp,
      event: 'delete',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_delete = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5962862420439144_delete_a4qx9e42l],
    event: 'delete',
  });
  const act_5962862420439144_script_r9uuumm2k = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_r9uuumm2k_getActionArgs,
  });
  const inst_evh_5962862420439144_serviceSelected_wlcjv7j0f =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_r9uuumm2k,
      event: 'service:selected',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_serviceSelected =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_serviceSelected_wlcjv7j0f],
      event: 'service:selected',
    });
  const act_5962862420439144_script_1w1nhlcuq = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_1w1nhlcuq_getActionArgs,
  });
  const inst_evh_5962862420439144_serviceChange_tkr364344 =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_1w1nhlcuq,
      event: 'service:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_serviceChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_serviceChange_tkr364344],
      event: 'service:change',
    });
  const act_5962862420439144_script_tp30bhqwr = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_tp30bhqwr_getActionArgs,
  });
  const inst_evh_5962862420439144_quantityChange_tko02b11m =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_tp30bhqwr,
      event: 'quantity:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_quantityChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_quantityChange_tko02b11m],
      event: 'quantity:change',
    });
  const act_5962862420439144_script_sh0fz8mfh = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_sh0fz8mfh_getActionArgs,
  });
  const inst_evh_5962862420439144_unitPriceChange_f3rapjt7u =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_sh0fz8mfh,
      event: 'unit_price:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_unitPriceChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_unitPriceChange_f3rapjt7u],
      event: 'unit_price:change',
    });
  const act_5962862420439144_script_s85myjghy = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_s85myjghy_getActionArgs,
  });
  const inst_evh_5962862420439144_discountChange_2ajossgib =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_s85myjghy,
      event: 'discount:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_discountChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_discountChange_2ajossgib],
      event: 'discount:change',
    });
  const act_5962862420439144_script_4p9xw0h3m = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_4p9xw0h3m_getActionArgs,
  });
  const inst_evh_5962862420439144_netAmountChange_of7vo5gzx =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_4p9xw0h3m,
      event: 'net_amount:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_netAmountChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_netAmountChange_of7vo5gzx],
      event: 'net_amount:change',
    });
  const act_5962862420439144_script_auosoz811 = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_auosoz811_getActionArgs,
  });
  const inst_evh_5962862420439144_amountChange_hjqpmf2kn =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_auosoz811,
      event: 'amount:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_amountChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_amountChange_hjqpmf2kn],
      event: 'amount:change',
    });
  const act_5962862420439144_script_n6qvghmkf = new actionTypes.ScriptAction({
    actionArgs: act_5962862420439144_script_n6qvghmkf_getActionArgs,
  });
  const inst_evh_5962862420439144_taxCodeChange_imuxcsovo =
    new actionTypes.EventHandlerImpl({
      action: act_5962862420439144_script_n6qvghmkf,
      event: 'tax_code:change',
      displayName: 'script',
    });
  const inst_evh_5962862420439144_taxCodeChange =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_5962862420439144_taxCodeChange_imuxcsovo],
      event: 'tax_code:change',
    });

  const actions = {
    inst_evh_5962862420439144_saveCompleted_mlcuqt6vm,
    inst_evh_5962862420439144_saveCompleted,
    inst_evh_5962862420439144_save_grg1k7es4,
    inst_evh_5962862420439144_save,
    inst_evh_5962862420439144_delete_a4qx9e42l,
    inst_evh_5962862420439144_delete,
    inst_evh_5962862420439144_serviceSelected_wlcjv7j0f,
    inst_evh_5962862420439144_serviceSelected,
    inst_evh_5962862420439144_serviceChange_tkr364344,
    inst_evh_5962862420439144_serviceChange,
    inst_evh_5962862420439144_quantityChange_tko02b11m,
    inst_evh_5962862420439144_quantityChange,
    inst_evh_5962862420439144_unitPriceChange_f3rapjt7u,
    inst_evh_5962862420439144_unitPriceChange,
    inst_evh_5962862420439144_discountChange_2ajossgib,
    inst_evh_5962862420439144_discountChange,
    inst_evh_5962862420439144_netAmountChange_of7vo5gzx,
    inst_evh_5962862420439144_netAmountChange,
    inst_evh_5962862420439144_amountChange_hjqpmf2kn,
    inst_evh_5962862420439144_amountChange,
    inst_evh_5962862420439144_taxCodeChange_imuxcsovo,
    inst_evh_5962862420439144_taxCodeChange,
  };

  const evh_5962862420439144_saveCompleted_mlcuqt6vm = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_saveCompleted_mlcuqt6vm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_saveCompleted = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_saveCompleted.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_save_grg1k7es4 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_save_grg1k7es4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_save = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_save.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete_a4qx9e42l = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete_a4qx9e42l.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_delete = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_delete.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_serviceSelected_wlcjv7j0f = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_serviceSelected_wlcjv7j0f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_serviceSelected = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_serviceSelected.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_serviceChange_tkr364344 = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_serviceChange_tkr364344.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_serviceChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_serviceChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_quantityChange_tko02b11m = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_quantityChange_tko02b11m.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_quantityChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_quantityChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_unitPriceChange_f3rapjt7u = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_unitPriceChange_f3rapjt7u.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_unitPriceChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_unitPriceChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_discountChange_2ajossgib = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_discountChange_2ajossgib.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_discountChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_discountChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_netAmountChange_of7vo5gzx = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_5962862420439144_netAmountChange_of7vo5gzx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_netAmountChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_netAmountChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_amountChange_hjqpmf2kn = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_amountChange_hjqpmf2kn.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_amountChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_amountChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_taxCodeChange_imuxcsovo = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_taxCodeChange_imuxcsovo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5962862420439144_taxCodeChange = async (event, scope) => {
    return await actions.inst_evh_5962862420439144_taxCodeChange.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox direction="column" alignItems="stretch" v-if="loaded">
    <ItpBox class="pb-2">
      <strong>
        {{ pgettext('Worksheet', 'Works') }}
      </strong>
    </ItpBox>
    <ItpDataTable
      rowKey="id"
      tableId="WorksheetWorkItems"
      :rowHeader="b_5962862420439144_rowHeader"
      :paging="b_5962862420439144_paging"
      :sortable="b_5962862420439144_sortable"
      :editable="!isReadonly"
      :value="worksheet.work_items"
      variant="details"
      :rowTemplate="{
        worksheet_id: worksheet.id,
        task: null,
        service_id: null,
      }"
      :columns="b_5962862420439144_columns"
      class="h-100"
      v-if="loaded"
      @save-completed="evh_5962862420439144_saveCompleted($event, {})"
      @save="evh_5962862420439144_save($event, {})"
      @delete="evh_5962862420439144_delete($event, {})"
      @service:selected="evh_5962862420439144_serviceSelected($event, {})"
      @service:change="evh_5962862420439144_serviceChange($event, {})"
      @quantity:change="evh_5962862420439144_quantityChange($event, {})"
      @unit_price:change="evh_5962862420439144_unitPriceChange($event, {})"
      @discount:change="evh_5962862420439144_discountChange($event, {})"
      @net_amount:change="evh_5962862420439144_netAmountChange($event, {})"
      @amount:change="evh_5962862420439144_amountChange($event, {})"
      @tax_code:change="evh_5962862420439144_taxCodeChange($event, {})"
    >
    </ItpDataTable>
  </ItpBox>
</template>
